import React, { useCallback, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { StoreState } from '@mm/state/store-interfaces';
import AttributeCRUDActions from '@mm/state/actions/AttributeCRUDActions';
import AttributeModel from '@mm/state/model/AttributeModel';
import { SlideoutMenu as Menu } from '../../components/SlideoutMenu';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { nonSystemAttributesSelector } from '@mm/state/selectors/attributeSelectors';

type PropsFromState = ReturnType<typeof mapStateToProps>;
type PropsFromDispatch = ReturnType<typeof mapDispatchToProps>;
type OuterProps = {
  searchQuery: string;
  showAddForm: boolean;
};

type Props = PropsFromState & PropsFromDispatch & OuterProps;

const SlideoutAttributes = ({
  searchQuery,
  showAddForm,
  fbPageId,
  retrieveAttributes,
  deleteAttribute,
  createAttribute,
  nonSystemAttributes,
}: Props) => {
  const onDeleteAttribute = useCallback(
    (attribute: AttributeModel): Promise<AttributeModel> =>
      deleteAttribute(attribute.id, fbPageId),
    [],
  );

  useEffect(() => {
    if (fbPageId) {
      retrieveAttributes(fbPageId);
    }
  }, [fbPageId]);

  const filteredAttributes = useMemo(
    () =>
      nonSystemAttributes.filter(
        attr =>
          !searchQuery ||
          (searchQuery &&
            attr.name.toLowerCase().indexOf(searchQuery.toLowerCase()) >= 0),
      ),
    [nonSystemAttributes, searchQuery],
  );

  const onAddAttribute = (name: string) => {
    createAttribute(name, fbPageId);
  };

  return (
    <>
      {showAddForm && (
        <Menu.AddForm placeholder="New attribute" onAdd={onAddAttribute} />
      )}
      <Menu.Content>
        {filteredAttributes.map(attribute => (
          <Menu.Item
            key={attribute.id}
            item={attribute}
            onDelete={onDeleteAttribute}
          />
        ))}
      </Menu.Content>
    </>
  );
};

const mapStateToProps = (state: StoreState) => ({
  attributes: state.attribute.entities || [],
  fbPageId: activeFacebookPageIdSelector(state),
  nonSystemAttributes: nonSystemAttributesSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  retrieveAttributes: (pageId: number) =>
    dispatch(AttributeCRUDActions.retrieveAll(pageId)),
  deleteAttribute: (attributeId: number, fbPageId: number | undefined) =>
    dispatch(AttributeCRUDActions.delete(fbPageId, attributeId)),
  createAttribute: (name: string, fbPageId: number) =>
    dispatch(AttributeCRUDActions.create(fbPageId, { name })),
});

const SlideoutAttributesWithState = connect<
  PropsFromState,
  PropsFromDispatch,
  OuterProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(SlideoutAttributes);

export { SlideoutAttributesWithState as SlideoutAttributes };
